import React, { useEffect, useMemo, useState } from "react";
import StyledModal from "../../components/CustomModal";
import { AddAnotherConditionalStyledButton, AddConditionalStyledButton, AddFieldWrapper, FooterButtons, StyledCollapse, StyledConditionalContainer, StyledConditionalRemoveButton, StyledOptionContainer, StyledOptionWrapper, StyledTypography } from "./styles";
import { Form, FormInstance, Input, Tag, Select, Space, Button, Checkbox, Collapse } from "antd";
import { DeleteFilled, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { selectCategories } from "../../redux/features/category/categorySlice";
import { selectSubCategories } from "../../redux/features/subCategory/subCategorySlice";
import { CheckboxQuestions, EstablishmentTypes, SubQuestion, SubQuestionTypes } from "../../types/QuestionTypes";
import { selectQuestion } from "../../redux/features/question/questionSlice";
import { FormWrapper, StyledButton } from "../../components/styles";
import { CollapseProps } from 'antd';
import DynamicOptionForm from "./DynamicOptionForm";
import { transformCheckboxData, formattedOptions, removeMatchingKeys, sortByTrueFirst } from "../../utils/helpers/dataTransformer";

const languageOptions = [
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "fr", label: "French" },
    { code: "de", label: "German" },
    { code: "pt", label: "Portuguese" },
    { code: "ar", label: "Arabic" },
];

type ModalProps = {
    isModalOpen: boolean;
    handleSave: (v: any) => void;
    onCancel: () => void;
    form: FormInstance<any>;
    hasAddPermission: boolean,
    hasEditPermission: boolean,
    hasViewPermission: boolean
};

const initialCheckboxQuestions: CheckboxQuestions[] = [{ id: 1, name: "true", subQuestion: [{ id: 1, fieldName: "", fieldType: 1, isRequired: false, }] }, { id: 1, name: "false", subQuestion: [{ id: 1, fieldName: "", fieldType: 1, isRequired: false, }] }];

const CreateEditModal: React.FC<ModalProps> = ({ isModalOpen, handleSave, onCancel, form, hasAddPermission, hasEditPermission, hasViewPermission }) => {
    const { loading } = useAppSelector(selectQuestion);
    const { data: parentCategories, loading: parentCategoriesLoading } = useAppSelector(selectCategories);
    const { data: subCategories, loading: subCategoriesLoading } = useAppSelector(selectSubCategories);

    const [activeKey, setActiveKey] = useState<string | string[]>(["1"]);
    const [typeValue, setTypeValue] = useState<number | string>();

    const [questions, setQuestions] = useState<CheckboxQuestions[]>(initialCheckboxQuestions);

    const parentCategoryOptions = useMemo(() => parentCategories?.map((c) => ({ label: c.name, value: c.id })) || [], [parentCategories]);

    const selectedCategory = Form.useWatch(["category"], form);
    const selectedSubCategory = Form.useWatch(["subCategory"], form);
    const canSaveChanges = useMemo(() => hasAddPermission || hasEditPermission, [hasAddPermission, hasEditPermission]);

    const parseTranslations = (value: any) => {
        if (!value) return {};
        if (typeof value === "string") {
            try {
                return JSON.parse(value) || {};
            } catch {
                return {};
            }
        }
        return value;
    };

    // Populate options when editing
    useEffect(() => {
        if (form.getFieldValue('id') && isModalOpen) {
            setTypeValue(form.getFieldValue('fieldType'));
            const currentOptions = form.getFieldValue("options");
            const hasCheckboxField = form.getFieldValue('fieldType') === EstablishmentTypes.Checkbox;
            if (currentOptions && Array.isArray(currentOptions) && hasCheckboxField) {
                setQuestions(sortByTrueFirst(currentOptions));
            } else {
                setQuestions(initialCheckboxQuestions);
            }
        } else if (!form.getFieldValue('id')) {
            setTypeValue("");
            setQuestions(initialCheckboxQuestions);
        }
    }, [form.getFieldValue('id'), isModalOpen, form]);

    useEffect(() => {
        if (isModalOpen) {
            const record = form.getFieldsValue(true);
            form.setFieldsValue({
                translationKey: record?.translationKey || undefined,
                fieldNameTranslations: parseTranslations(record?.fieldNameTranslations),
                placeholderTranslations: parseTranslations(record?.placeholderTranslations),
            });
        }
    }, [isModalOpen, form]);

    const onChange = (key: string | string[]) => {
        setActiveKey(key);
    };

    // Handle adding new conditional questions
    const handleAddCondition = (type: 'true' | 'false') => {
        setQuestions((prev) => {
            // Find the question for the current type ('true' or 'false')
            return prev.map((question) => {
                if (question.name === type) {
                    const newSubQuestion = {
                        id: (question.subQuestion.length ?? 0) + 1,
                        fieldName: '',
                        fieldType: 1,
                        isRequired: false,
                    };
                    return {
                        ...question,
                        subQuestion: [...question.subQuestion, newSubQuestion],
                    };
                }
                return question;
            });
        });
    };

    // Handle removing conditional questions
    const handleRemoveCondition = (type: 'true' | 'false', id: number | string) => {
        setQuestions((prev) => {
            return prev.map((question) => {
                if (question.name === type) {
                    return {
                        ...question,
                        subQuestion: question.subQuestion.filter((item) => item.id !== id),
                    };
                }
                return question;
            });
        });
    };

    // Handle input field change (fieldName, fieldType, isRequired)
    const handleChange = (
        type: 'true' | 'false',
        id: number | string,
        field: keyof SubQuestion,
        value: string | boolean | number
    ) => {
        setQuestions((prev) => {
            return prev.map((question) => {
                if (question.name === type) {
                    const updatedSubQuestions = question.subQuestion.map((item) =>
                        item.id === id ? { ...item, [field]: value } : item
                    );
                    return {
                        ...question,
                        subQuestion: updatedSubQuestions,
                    };
                }
                return question;
            });
        });
    };

    useEffect(() => {
        form.setFieldValue("options", transformCheckboxData(questions));
    }, [form, questions]);

    const items: CollapseProps['items'] = [
        {
            key: '1',
            label: <>{activeKey && Array.isArray(activeKey) && activeKey.length > 0 ? <> <MinusOutlined /> Remove Conditional Question </> : <> <PlusOutlined /> Add Conditional Question </>}</>,
            children: <Form.Item name="options">
                {/* Conditional Sections */}
                {questions?.map((type) => {
                    return (
                        <div key={type.name} style={{ padding: "16px 0px", borderTop: `${type.name === "false" && `1px dashed #154075`}` }}>
                            <StyledTypography>{type.name === "true" ? "if True" : "if False"}</StyledTypography>
                            {type?.subQuestion?.map((q, index) => {
                                return (
                                    <StyledConditionalContainer key={q.id} className="conditional-item" style={{ marginBottom: "10px" }}>
                                        <AddFieldWrapper>
                                            <div>#{index + 1}</div>
                                            <StyledConditionalRemoveButton onClick={() => handleRemoveCondition(type.name, q.id)}>
                                                <MinusOutlined /> Remove
                                            </StyledConditionalRemoveButton>
                                        </AddFieldWrapper>
                                        <Form.Item
                                            label="Name"
                                            name={[type.name, q.id, "fieldName"]}
                                            initialValue={q.fieldName}
                                            rules={[{ required: true, message: "Field name is required" }]}
                                            style={{ width: "100%" }}
                                        >
                                            <Input
                                                size="large"
                                                placeholder="Field name"
                                                value={q.fieldName}
                                                onChange={(e) => handleChange(type.name, q.id, "fieldName", e.target.value)}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label="Type"
                                            name={[type.name, q.id, "fieldType"]}
                                            initialValue={q.fieldType}
                                            rules={[{ required: true, message: "Field type is required" }]}
                                            style={{ width: "100%" }}
                                        >
                                            <Select
                                                size="large"
                                                options={Object.entries(SubQuestionTypes).map(([key, value]) => ({
                                                    label: key,
                                                    value: value,
                                                }))}
                                                placeholder="Select field type"
                                                onChange={(value) => {
                                                    handleChange(type.name, q.id, "fieldType", value)
                                                }}
                                            />
                                        </Form.Item>

                                        <Form.Item style={{ width: "100%" }} name={[type.name, q.id, "isRequired"]} valuePropName="checked" initialValue={q.isRequired}>
                                            <Checkbox
                                                checked={q.isRequired}
                                                onChange={(e) =>
                                                    handleChange(type.name, q.id, "isRequired", e.target.checked)
                                                }
                                            >
                                                Is Required
                                            </Checkbox>
                                        </Form.Item>
                                    </StyledConditionalContainer>
                                )
                            })}

                            <AddAnotherConditionalStyledButton onClick={() => handleAddCondition(type.name)}>
                                <PlusOutlined /> Add Another Conditional Question
                            </AddAnotherConditionalStyledButton>
                        </div>
                    )
                })}
            </Form.Item>,
        },
    ];

    return (
        <StyledModal
            draggable={true}
            width={650}
            title={form.getFieldValue('id') ? `${canSaveChanges ? "Edit" : "View"} Category Question` : `${canSaveChanges ? "Add" : "View"} Category Question`}
            open={isModalOpen}
            closeIcon={null}
            footer={
                <FooterButtons>
                    <StyledButton onClick={() => {
                        onCancel();
                        setQuestions(initialCheckboxQuestions);
                    }} disabled={loading}>
                        Cancel
                    </StyledButton>
                    {canSaveChanges && <StyledButton
                        type="primary"
                        loading={loading}
                        onClick={async () => {
                            try {
                                const values = await form.validateFields();
                                const cleanedValues = removeMatchingKeys(values);
                                const formattedOption = cleanedValues?.fieldType === EstablishmentTypes.Option ? formattedOptions(cleanedValues?.options) : cleanedValues?.options;

                                handleSave({
                                    ...cleanedValues,
                                    options: formattedOption,
                                    isRequired: !!values.isRequired,
                                });
                            } catch (error) {
                                console.error("Validation failed:", error);
                            }
                        }}
                    >
                        Save
                    </StyledButton>}
                </FooterButtons>
            }
            centered
            onCancel={() => !loading && onCancel()}

        >
            <FormWrapper form={form} layout="vertical" disabled={loading || !canSaveChanges}>
                <Form.Item label="Parent Category" name="category" rules={[{ required: true, message: "Please select a parent category" }]}>
                    <Select size="large" placeholder="Select Parent Category" options={parentCategoryOptions} loading={parentCategoriesLoading} />
                </Form.Item>
                {selectedCategory && (
                    <Form.Item label="Sub Category" name="subCategory" rules={[{ required: true, message: "Please select a sub category" }]}>
                        <Select
                            size="large"
                            loading={subCategoriesLoading}
                            placeholder="Select Sub Category"
                            options={subCategories
                                ?.filter((c) => c?.category?.id === selectedCategory)
                                .map((subCategory) => ({
                                    label: subCategory.name,
                                    value: subCategory.id,
                                }))}
                        />
                    </Form.Item>
                )}
                {selectedSubCategory && (
                    <>
                        <Form.Item label="Name" name="fieldName" rules={[{ required: true, message: "Field name is required" }]}>
                            <Input size="large" placeholder="Field name" />
                        </Form.Item>
                        <Form.Item label="Translation Key" name="translationKey" tooltip="Optional i18n key for this question">
                            <Input size="large" placeholder="e.g. category.question.serialNumber" />
                        </Form.Item>
                        <div style={{ marginBottom: 8, fontWeight: 600 }}>Localized Names</div>
                        <Space direction="vertical" style={{ width: "100%" }}>
                            {languageOptions.map((lang) => (
                                <Form.Item key={lang.code} name={["fieldNameTranslations", lang.code]} label={lang.label}>
                                    <Input size="large" placeholder={`Enter ${lang.label} name`} />
                                </Form.Item>
                            ))}
                        </Space>
                        <div style={{ marginBottom: 8, fontWeight: 600 }}>Localized Placeholders</div>
                        <Space direction="vertical" style={{ width: "100%" }}>
                            {languageOptions.map((lang) => (
                                <Form.Item key={lang.code} name={["placeholderTranslations", lang.code]} label={lang.label}>
                                    <Input size="large" placeholder={`Enter ${lang.label} placeholder`} />
                                </Form.Item>
                            ))}
                        </Space>
                        <Form.Item label="Type" name="fieldType" rules={[{ required: true, message: "Field type is required" }]}>
                            <Select
                                size="large"
                                options={Object.entries(EstablishmentTypes).map(([key, value]) => ({
                                    label: key,
                                    value: value,
                                }))}
                                placeholder="Select field type"
                                onChange={(value) => {
                                    setTypeValue(value);
                                }}
                            />
                        </Form.Item>

                        {typeValue === EstablishmentTypes.Option && <DynamicOptionForm form={form} />}

                        <Form.Item name="isRequired" valuePropName="checked">
                            <Checkbox>Is Required</Checkbox>
                        </Form.Item>
                        {typeValue === EstablishmentTypes.Checkbox && <StyledCollapse expandIcon={() => null} items={items} activeKey={activeKey} onChange={onChange} />}
                    </>
                )}
            </FormWrapper>

        </StyledModal>
    );
};

export default CreateEditModal;
